import { EVENT_DIMENSION_NAME } from './constants';

// helper to get the text for a crumb label or name
const crumbText = (crumb) => crumb?.label || crumb?.name;

export const normalizeBreadcrumb = (crumb) => {
  return {
    id: null,
    name: null,
    label: null,
    refinementKey: null,
    dimensionName: 'Category',
    ...crumb
  };
};

export const parseSearchModelForCrumbs = (keyword, crumbs = []) => {
  let existingCrumbs = [...crumbs];

  if (keyword) {
    const textSearchCrumb = {
      url: '',
      label: 'Text Search'
    };

    let label;
    try {
      label = decodeURIComponent(keyword);
    } catch (err) {
      label = keyword;
    }
    const keywordCrumb = {
      url: `/s/${keyword}?NCNI-5`,
      label
    };
    const alreadyHasTextSearch = existingCrumbs.some((crumb) => crumb.label === textSearchCrumb.label);
    if (!alreadyHasTextSearch) {
      existingCrumbs = [textSearchCrumb, keywordCrumb, ...existingCrumbs];
    }
  }

  if (!existingCrumbs.length) return null;

  const categoryOnly = existingCrumbs.filter(
    (crumb) => (crumb.dimensionName || '').toLowerCase() === 'category'
  );

  // When more than one Category crumb exists, the taxonomy is
  // resolved, so keep only Category crumbs. Otherwise fall back to all
  // selected crumbs.
  const resolved = categoryOnly.length > 1
    ? categoryOnly
    : existingCrumbs.filter((crumb) => !crumb.deselectUrl);

  return resolved.map(normalizeBreadcrumb);
};

const filterEmptyBreadcrumb = (breadcrumbs = []) => (breadcrumbs || []).filter(crumbText);

export const hasNoCategoryDimension = (breadcrumbs = []) => !breadcrumbs.some(
  (crumb) => (crumb.dimensionName || '').toLowerCase() === 'category'
);

const filterEventBreadcrumbsForMobile = (shouldShowEventOnly, breadcrumbs = []) => (shouldShowEventOnly
  ? breadcrumbs.filter((crumb) => crumb.dimensionName === EVENT_DIMENSION_NAME)
  : breadcrumbs.filter((crumb) => crumb.dimensionName !== EVENT_DIMENSION_NAME));

// Mobile-only: hide breadcrumbs on search pages that have no category
export const shouldHideOnNoCategory = ({ isMobile, experiencePath, customContext }) => {
  if (!isMobile) return false;
  // Experience context path does not update on filter change
  // If they differ, prefer the custom context path
  const correctPath = customContext?.path && experiencePath !== customContext.path
    ? customContext.path
    : experiencePath;
  return correctPath && (!!correctPath.match('/s/') || /Ntt-/.test(correctPath));
};

// Pull the raw breadcrumb array
export const getBreadcrumbsFromData = (data, isMobile, shouldShowEventOnly) => {
  if (data?.product?.taxonomy?.breadCrumbs) {
    const raw = data.product.taxonomy.breadCrumbs;

    return isMobile
      ? filterEventBreadcrumbsForMobile(shouldShowEventOnly, raw)
      : raw;
  }

  if (data?.searchModel?.taxonomy?.breadCrumbs) {
    return data.searchModel.taxonomy.breadCrumbs;
  }

  if (data?.collection?.taxonomy?.breadCrumbs) {
    return data.collection.taxonomy.breadCrumbs;
  }

  return [];
};

export const getSearchKeyword = (data) => data?.searchModel?.searchReport?.keyword || null;

export const getBrand = (data) => {
  const brandName = data?.product?.identifiers?.brandName;
  const brandLinkUrl = data?.product?.taxonomy?.brandLinkUrl;
  return brandName && brandLinkUrl
    ? { name: brandName, linkUrl: brandLinkUrl }
    : null;
};

const resolveProductCrumbs = (breadcrumbs, shouldLastCrumbBeLinkableOverride) => {
  const crumbs = (breadcrumbs || []).map(normalizeBreadcrumb);
  if (!crumbs.length) return crumbs;
  return crumbs.map((crumb, i) => (
    i === crumbs.length - 1
      ? { ...crumb, shouldLastCrumbBeLinkable: shouldLastCrumbBeLinkableOverride }
      : crumb
  ));
};

const resolveSearchCrumbs = (breadcrumbs, keyword, pathname) => {
  const crumbs = parseSearchModelForCrumbs(keyword, breadcrumbs);
  if (!crumbs?.length) return crumbs;
  if (pathname?.startsWith('/b/')) {
    return crumbs.map((crumb, i) => (
      i === crumbs.length - 1
        ? { ...crumb, shouldLastCrumbBeLinkable: true }
        : crumb
    ));
  }
  return crumbs;
};

// Append brand crumb for PIP pages. Takes name and linkUrl
// The appended brand crumb renders as a link
const appendBrandCrumb = (crumbs, brand) => {
  if (!brand || !crumbs?.length) return crumbs;

  const last = crumbs[crumbs.length - 1];
  const brandCrumb = {
    ...last,
    label: `${brand.name} ${last.label || last.name}`,
    name: `${brand.name} ${last.name || last.label}`,
    url: brand.linkUrl,
    browseUrl: brand.linkUrl
  };
  return [...crumbs, brandCrumb];
};

const prependHomeCrumb = (crumbs, crumbAttribute) => {
  const crumbsWithHome = crumbs[0]?.[crumbAttribute] === 'Home'
    ? crumbs
    : [{ [crumbAttribute]: 'Home', url: '/' }, ...crumbs];
  return crumbsWithHome.filter((crumb, idx) => !(crumbText(crumb) === 'Home' && idx > 0));
};

const toRenderItems = ({ crumbs, crumbAttribute, shouldShowEventOnly, itemId }) => {
  const lastIndex = crumbs.length - 1;
  return crumbs.map((crumb, i) => ({
    text: crumb.isInternetNumber ? `Internet #${itemId}` : crumb[crumbAttribute],
    href: crumb.browseUrl || crumb.url,
    isLinkable: !crumb.isInternetNumber
      && (i !== lastIndex || crumb.shouldLastCrumbBeLinkable === true),
    showSeparator: i > 0,
    separator: shouldShowEventOnly ? '|' : '/',
    isInternetNumber: crumb.isInternetNumber === true,
    isEventDimension: crumb.dimensionName === EVENT_DIMENSION_NAME,
    creativeIconUrl: crumb.creativeIconUrl || null
  }));
};

const resolveBaseCrumbs = ({
  staticCrumbs,
  itemId,
  breadcrumbs,
  keyword,
  pathname,
  shouldLastCrumbBeLinkableOverride,
  brand
}) => {
  if (staticCrumbs) {
    return staticCrumbs.map(normalizeBreadcrumb);
  }

  let crumbs = itemId
    ? resolveProductCrumbs(
      breadcrumbs,
      shouldLastCrumbBeLinkableOverride
    )
    : resolveSearchCrumbs(
      breadcrumbs,
      keyword,
      pathname
    );

  crumbs = filterEmptyBreadcrumb(crumbs);

  return appendBrandCrumb(crumbs, brand);
};

const applyDisplayRules = ({
  crumbs,
  crumbAttribute,
  isMobile,
  shouldShowEventOnly,
  showInternet,
  itemId
}) => {
  let result = prependHomeCrumb(
    crumbs,
    crumbAttribute
  );

  if (isMobile && shouldShowEventOnly) {
    result = result.slice(1);
  }

  if (showInternet) {
    result = [
      ...result,
      {
        isInternetNumber: true,
        label: `Internet #${itemId}`
      }
    ];
  }

  return result;
};

/**
 * Builds the final render-ready crumb list.
 * Returns { items, crumbAttribute }.
 */
export const buildCrumbs = ({
  breadcrumbs,
  itemId,
  keyword,
  brand,
  shouldLastCrumbBeLinkableOverride,
  pathname,
  staticCrumbs,
  isMobile,
  shouldShowEventOnly,
  showInternet
}) => {
  let crumbs = resolveBaseCrumbs({
    staticCrumbs,
    itemId,
    breadcrumbs,
    keyword,
    pathname,
    shouldLastCrumbBeLinkableOverride,
    brand
  });

  if (!crumbs?.length) {
    return null;
  }

  const crumbAttribute = crumbs[0].name ? 'name' : 'label';

  crumbs = applyDisplayRules({
    crumbs,
    crumbAttribute,
    isMobile,
    shouldShowEventOnly,
    showInternet,
    itemId
  });

  return {
    items: toRenderItems({
      crumbs,
      crumbAttribute,
      shouldShowEventOnly,
      itemId
    }),
    crumbAttribute
  };
};